<template>
	<div id="app">
		<component class="" :is="layout" v-if="layout" />
		<notification-permission-banner v-if="user" />
	</div>
</template>

<script>
// import gtm from '@/plugins/gtm'
import $ from "jquery";
import NotificationPermissionBanner from "@/components/NotificationPermissionBanner.vue";
import { SET_UNREAD_NOTIFICATION } from "@/store/mutation-types";
import { useWorkFlowConfig } from "@/store/workFlowConfig";

const UNREAD_FALLBACK_SYNC_INTERVAL = 60000;

export default {
	components: {
		auth: () => import(/* webpackChunkName: "auth" */ "@/layouts/Auth.vue"),
		default: () =>
			import(/* webpackChunkName: "default" */ "@/layouts/Default.vue"),
		certificate_lookup: () =>
			import(
				/* webpackChunkName: "certificate_lookup" */ "@/layouts/CertificateLookup.vue"
			),
		NotificationPermissionBanner,
	},
	data() {
		return {
			fcmForegroundReady: false,
			unreadSyncTimer: null,
			unreadSyncVisibilityHandler: null,
			lastKnownUnread: null,
			serviceWorkerMessageHandler: null,
		};
	},
	computed: {
		layout() {
			let layout = this.$store.state.layout || "default";
			if (
				this.$route &&
				this.$route.meta &&
				this.$route.meta.layout &&
				this.$route.meta.layout === "certificate_lookup"
			) {
				layout = "certificate_lookup";
			}
			return layout;
		},
		user() {
			return !!this.$store.state.profile;
		},
	},
	watch: {
		user(val) {
			if (val) {
				this.setupFcmForeground();
				this.registerFcmIfAlreadyGranted();
				this.startUnreadSync();
			} else {
				this.stopUnreadSync();
			}
		},
	},
	created() {
		// // console.log('gtm', gtm)
		// gtm.enable(true)
	},
	mounted() {
		this.bindNotificationSoundUnlock();
		this.bindServiceWorkerMessages();
		if (process.env.CLIENT_ENV !== 'dev') {
			$("head")
				.append(`<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
			new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
			j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
			'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
			})(window,document,'script','dataLayer','${process.env.GOOGLE_TAG_MANAGER}');<\/script>`);
					$("body")
						.append(`<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=${process.env.GOOGLE_TAG_MANAGER}"
			height="0" width="0" style="display:none;visibility:hidden"><\/iframe></noscript>`);
		}
		if (this.user) {
			this.setupFcmForeground();
			this.registerFcmIfAlreadyGranted();
			this.startUnreadSync();
		}
	},
	beforeDestroy() {
		this.stopUnreadSync();
		this.unbindServiceWorkerMessages();
	},
	methods: {
		bindServiceWorkerMessages() {
			if (!('serviceWorker' in navigator)) {
				return;
			}
			this.serviceWorkerMessageHandler = (event) => {
				const message = event && event.data ? event.data : {};
				if (message.type !== 'FCM_BACKGROUND_MESSAGE') {
					return;
				}
				this.handleRealtimeNotification(message.payload || {});
			};
			navigator.serviceWorker.addEventListener('message', this.serviceWorkerMessageHandler);
		},
		unbindServiceWorkerMessages() {
			if (this.serviceWorkerMessageHandler && 'serviceWorker' in navigator) {
				navigator.serviceWorker.removeEventListener('message', this.serviceWorkerMessageHandler);
			}
			this.serviceWorkerMessageHandler = null;
		},
		async setupFcmForeground() {
			if (this.fcmForegroundReady) {
				return;
			}
			this.fcmForegroundReady = true;
			let fcm;
			let sound;
			try {
				fcm = await import("@/services/firebase-messaging");
				sound = await import("@/services/notification-sound");
			} catch (e) {
				return;
			}
			if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
				await fcm.initFcm();
			}
			fcm.onForegroundMessage((payload) => {
				console.info('[FCM] Foreground message received.', payload);
				this.handleRealtimeNotification(payload || {}, sound);
			});
		},
		async handleRealtimeNotification(payload, soundModule = null) {
			this.setUnreadCount((this.$store.getters.unreadNotification || 0) + 1);
			this.syncUnreadNotifications(false);
			if (document.visibilityState !== 'visible') {
				return;
			}
			try {
				const sound = soundModule || await import("@/services/notification-sound");
				sound.playNotificationSound();
			} catch (e) {
				// Audio is best-effort.
			}
			this.showForegroundNotification(payload || {});
		},
		setUnreadCount(count) {
			const value = Number(count) || 0;
			this.$store.commit(SET_UNREAD_NOTIFICATION, value);
			useWorkFlowConfig().setNoti(value);
		},
		startUnreadSync() {
			this.stopUnreadSync();
			this.bindUnreadSyncEvents();
			this.syncUnreadNotifications(false);
			this.scheduleUnreadFallbackSync();
		},
		stopUnreadSync() {
			if (this.unreadSyncTimer) {
				clearTimeout(this.unreadSyncTimer);
				this.unreadSyncTimer = null;
			}
			this.unbindUnreadSyncEvents();
		},
		scheduleUnreadFallbackSync() {
			if (this.unreadSyncTimer) {
				clearTimeout(this.unreadSyncTimer);
			}
			this.unreadSyncTimer = setTimeout(async () => {
				this.unreadSyncTimer = null;
				if (this.user && document.visibilityState === 'visible') {
					await this.syncUnreadNotifications(true);
				}
				if (this.user) {
					this.scheduleUnreadFallbackSync();
				}
			}, UNREAD_FALLBACK_SYNC_INTERVAL);
		},
		bindUnreadSyncEvents() {
			if (this.unreadSyncVisibilityHandler) {
				return;
			}
			this.unreadSyncVisibilityHandler = () => {
				if (document.visibilityState !== 'visible') {
					return;
				}
				this.syncUnreadNotifications(true);
				this.scheduleUnreadFallbackSync();
			};
			document.addEventListener('visibilitychange', this.unreadSyncVisibilityHandler);
			window.addEventListener('focus', this.unreadSyncVisibilityHandler);
		},
		unbindUnreadSyncEvents() {
			if (!this.unreadSyncVisibilityHandler) {
				return;
			}
			document.removeEventListener('visibilitychange', this.unreadSyncVisibilityHandler);
			window.removeEventListener('focus', this.unreadSyncVisibilityHandler);
			this.unreadSyncVisibilityHandler = null;
		},
		async syncUnreadNotifications(notifyOnIncrease) {
			const user = this.$store.state.profile && this.$store.state.profile.data
				? this.$store.state.profile.data.user
				: null;
			if (!user || !user.id) {
				return;
			}

			try {
				const { default: NotificationModel } = await import("@/models/Notification");
				const resp = await NotificationModel.getUnreadCount(user.id);
				const count = Number(resp.data.unreadNotifications) || 0;
				const previous = this.lastKnownUnread;
				this.lastKnownUnread = count;
				this.setUnreadCount(count);

				if (notifyOnIncrease && previous !== null && count > previous) {
					this.showUnreadIncreaseNotice(count - previous);
				}
			} catch (e) {
				// Fallback sync must not disturb the main app flow.
			}
		},
		async showUnreadIncreaseNotice(delta) {
			let sound;
			try {
				sound = await import("@/services/notification-sound");
				sound.playNotificationSound();
			} catch (e) {
				// Audio is best-effort.
			}
			this.showForegroundNotification({
				notification: {
					title: "Thông báo mới",
					body: delta > 1 ? `Bạn có ${delta} thông báo mới.` : "Bạn có 1 thông báo mới.",
				},
			});
		},
		notificationContent(payload) {
			const notification = payload.notification || {};
			const data = payload.data || {};
			const title = notification.title || data.title || data.subject || "Thông báo mới";
			const body = data.message || data.body || notification.body || "";
			const displayMessage = body || title;
			return { title, body, data, displayMessage };
		},
		showForegroundNotification(payload) {
			const { title, body, data, displayMessage } = this.notificationContent(payload);
			this.$toast.open({
				message: displayMessage,
				type: 'info',
				position: 'top-right',
				duration: 5000,
			});

			if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
				return;
			}

			try {
				const browserNotification = new Notification(title, {
					body,
					icon: '/static/ic_logo.png',
					data,
				});
				browserNotification.onclick = () => {
					window.focus();
					const url = data.web_path || '';
					if (url) {
						this.$router.push(url).catch(() => {});
					}
					browserNotification.close();
				};
			} catch (e) {
				// Some browsers restrict foreground system notifications; toast remains visible.
			}
		},
		async bindNotificationSoundUnlock() {
			let sound;
			try {
				sound = await import("@/services/notification-sound");
			} catch (e) {
				return;
			}
			const unlock = () => {
				sound.unlockNotificationSound();
				window.removeEventListener('click', unlock);
				window.removeEventListener('keydown', unlock);
				window.removeEventListener('touchstart', unlock);
			};
			window.addEventListener('click', unlock, { once: true });
			window.addEventListener('keydown', unlock, { once: true });
			window.addEventListener('touchstart', unlock, { once: true });
		},
		async registerFcmIfAlreadyGranted() {
			try {
				if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
					return;
				}
				const [{ initFcm }, { default: NotificationDevice }] = await Promise.all([
					import("@/services/firebase-messaging"),
					import("@/models/NotificationDevice"),
				]);
				const token = await initFcm();
				if (!token) {
					console.warn('[FCM] Token is empty; device was not registered.');
					return;
				}
				const deviceId = this.deviceId();
				await NotificationDevice.registerDevice({
					platform: 'web',
					fcm_token: token,
					device_id: deviceId,
					browser: this.browserName(),
					os: this.osName(),
				});
				console.info('[FCM] Notification device registered.', {
					device_id: deviceId,
					token_hash: await this.tokenFingerprint(token),
				});
			} catch (e) {
				console.warn('[FCM] Could not register notification device.', e);
			}
		},
		async tokenFingerprint(token) {
			if (!window.crypto || !window.crypto.subtle) {
				return token.slice(-12);
			}
			const bytes = new TextEncoder().encode(token);
			const digest = await window.crypto.subtle.digest('SHA-256', bytes);
			return Array.from(new Uint8Array(digest))
				.map((byte) => byte.toString(16).padStart(2, '0'))
				.join('')
				.slice(0, 12);
		},
		deviceId() {
			let id = localStorage.getItem('web_device_id');
			if (!id) {
				id = 'web-' + Math.random().toString(36).slice(2, 12);
				localStorage.setItem('web_device_id', id);
			}
			return id;
		},
		browserName() {
			try { return navigator.userAgent.match(/(Chrome|Firefox|Safari|Edge)\/[\d.]+/)?.[0] || ''; } catch (e) { return ''; }
		},
		osName() {
			try { return navigator.platform || ''; } catch (e) { return ''; }
		},
	},
};
</script>
<style lang="scss" scoped>
#app {
	min-height: 100%;
}
@media (max-width: 1023px) {
	.d-flex {
		display: block !important;
	}
}
.nav {
	@media (max-width: 1024px) {
		position: fixed;
	}
}
</style>
